/**
 * Copyright (c) 2017 CRF Box Oy. All Rights Reserved. Company Confidential.
 */

/* eslint complexity: 0 */
/* eslint react/display-name: 0 */

import DateHelper from '../../helper/date';
import React from 'react';
import * as consts from '../../const.js';
import cloneDeep from 'lodash/cloneDeep';
import { compose } from 'ramda';
import { Grid, Time } from 'tc-client-common';

import { generateActions, generateAddendumActions, createActions } from './utils/documents.service.utils';

import {
  VERSION,
  MANDATORY_FOR_ALL_LABEL,
  MANDATORY_FOR_SELECTED_LABEL,
  OPTIONAL_FOR_ALL_LABEL,
  OPTIONAL_FOR_SELECTED_LABEL,
  DELETE_PDF_TITLE,
  REMOVE_ENTIRE_DOCUMENT_FROM_LIST,
  REMOVE_DOCUMENT_FROM_LIST,
  SIGNED_ON_PAPER
} from '../../const';

const columns = {
  documentName: consts.DOCUMENT_NAME,
  consentingStatus: consts.STATUS,
  expirationDate: consts.EXPIRATION_DATE_UTC,
  signatureDate: consts.SIGNATURE_DATE_UTC,
  signed: consts.SIGNED_DOCUMENT_PDF,
  journal: consts.JOURNAL,
  actions: consts.ACTIONS,
  viewSignedPdf: consts.VIEW_SIGNED_PDF,
  viewPdf: consts.VIEW_PDF
};

const noSignedDocs = <h5>{consts.NO_SIGNED_DOCS}</h5>;
const noAssignedDocs = <h5>{consts.NO_ASSIGNED_DOCS}</h5>;
const noObsoleteDocs = <h5>{consts.NO_OBSOLETE_DOCS}</h5>;

export const delete_options = {
  pdf: 'pdf',
  document: 'doc',
  entireDocument: 'entireDoc'
};

const sortDocuments = (xs) =>
  xs.sort(({ signedTimestamp: a }, { signedTimestamp: b }) => {
    const dateA = Date(a);
    const dateB = Date(b);

    if (dateA > dateB) return 1;
    if (dateB > dateA) return -1;
    return 0;
  });

const appendAddendums = (documents) => {
  return documents.reduce((acc, doc) => {
    if (doc.addendums && doc.addendums.length > 0) {
      return acc.concat(doc).concat(
        doc.addendums.map((addendum, index, list) => ({
          ...addendum,
          addendum: true,
          lastAddendum: index === list.length - 1,
          consentingStatus: doc.consentingStatus,
          signedTimestamp: addendum.createdOn,
          consentingGid: doc.consentingGid,
          consentGid: doc.consentGid,
          permissions: doc.permissions
        }))
      );
    }

    return acc.concat(doc);
  }, []);
};

const prepareSignedGrid = (signedDocuments, documentsMetadata, actions, user, participant, timezone) => {
  if (!signedDocuments.length) return noSignedDocs;

  const columnDefinition = [
    { label: columns.documentName, size: 40 },
    { label: columns.consentingStatus, size: 25 },
    { label: columns.signatureDate, size: 25 },
    { label: columns.actions, size: 10 }
  ];

  const buildSignedRows = (documents) =>
    appendAddendums(documents).map((row, index) => {
      const id = row.consentGid;
      if (row.addendum) {
        return {
          id: `grid-row-${id}`,
          rowClass: 'addendum-row',
          columns: [
            {
              id: `${index}-${id}`,
              columnData: () => (
                <div className="addendum">
                  <span className={`addendum-line${row.lastAddendum ? ' last-addendum' : ''}`} />
                  <span className="addendum-separator" />
                  {consts.ADDENDUM} {row.name}
                </div>
              )
            },
            { id: `${index}-${id}`, columnData: () => '' },
            {
              id: `${index}-${id}`,
              columnData: () => <Time timestamp={row.signedTimestamp} format={'DD MMM YYYY'} />
            },
            { id: `${index}-${id}`, columnData: () => generateAddendumActions(row, actions, user, documentName) }
          ]
        };
      }

      let documentName = DocumentsService.buildDocumentFullName(row.consentName, row.documentVersion);
      const signedTimestamp =
        row.consentingStatus === 'SIGNED_ON_PAPER' ? row.signedTimestamp : row.originalSignatureDate;
      return {
        id: `grid-row-${id}`,
        rowClass: (row.addendums || []).length !== 0 ? 'has-addendums' : '',
        columns: [
          {
            id: `${index}-${id}`,
            columnData: () => DocumentsService.getDocumentNameColumn(row.documentConfiguration, documentName)
          },
          { id: `${index}-${id}`, columnData: () => consts[row.consentingStatus] },
          {
            id: `${index}-${id}`,
            columnData: () => <Time timestamp={signedTimestamp} format={'DD MMM YYYY'} />
          },
          { id: `${index}-${id}`, columnData: () => generateActions(row, actions, user, documentName, participant) }
        ]
      };
    });

  return (
    <div className="signed-documents-grid">
      <Grid
        gridId="signed-documents"
        columnDefinition={columnDefinition}
        rows={compose(buildSignedRows, sortDocuments, cloneDeep)(signedDocuments)}
      />
    </div>
  );
};

const prepareAssignedGrid = (
  assignedDocuments,
  documentsMetadata,
  actions,
  user,
  participant,
  participantDocumentActionsAllowed,
  timezone
) => {
  if (assignedDocuments.length === 0) return noAssignedDocs;

  const columnDefinition = [
    { label: columns.documentName, size: 40 },
    { label: columns.consentingStatus, size: 25 },
    { label: columns.expirationDate, size: 25 },
    { label: columns.actions, size: 10 }
  ];

  const buildAssignedRows = (documents) =>
    documents.map((row, index) => {
      let documentName = DocumentsService.buildDocumentFullName(row.consentName, row.documentVersion);
      const id = row.consentGid;

      return {
        id: `grid-row-${id}`,
        columns: [
          {
            id: `${index}-${id}`,
            columnData: () => DocumentsService.getDocumentNameColumn(row.documentConfiguration, documentName)
          },
          { id: `${index}-${id}`, columnData: () => consts[row.consentingStatus] },
          {
            id: `${index}-${id}`,
            columnData: () => <Time timestamp={row.expirationDate} format={'DD MMM YYYY'} />
          },
          {
            id: `${index}-${id}`,
            columnData: () =>
              createActions(
                row,
                actions,
                documentName,
                'assigned',
                user,
                participant,
                participantDocumentActionsAllowed
              )
          }
        ]
      };
    });

  return (
    <div className="assigned-documents-grid">
      <Grid
        gridId="assigned-documents"
        columnDefinition={columnDefinition}
        rows={buildAssignedRows(assignedDocuments)}
      />
    </div>
  );
};

const prepareObsoleteGrid = (
  obsoleteDocuments,
  documentsMetadata,
  actions,
  user,
  participant,
  participantDocumentActionsAllowed,
  timezone
) => {
  if (obsoleteDocuments.length === 0) return noObsoleteDocs;

  const columnDefinition = [
    { label: columns.documentName, size: 40 },
    { label: columns.consentingStatus, size: 25 },
    { label: columns.expirationDate, size: 25 },
    { label: columns.actions, size: 10 }
  ];

  const buildObsoleteRows = (documents) =>
    documents.map((row, index) => {
      let documentName = DocumentsService.buildDocumentFullName(row.consentName, row.documentVersion);
      const id = row.consentGid;

      return {
        id: `grid-row-${id}`,
        columns: [
          {
            id: `${index}-${id}`,
            columnData: () => DocumentsService.getDocumentNameColumn(row.documentConfiguration, documentName)
          },
          { id: `${index}-${id}`, columnData: () => consts[row.consentingStatus] },
          {
            id: `${index}-${id}`,
            columnData: () => <Time timestamp={row.expirationDate} format={'DD MMM YYYY'} />
          },
          {
            id: `${index}-${id}`,
            columnData: () =>
              createActions(
                row,
                actions,
                documentName,
                'obsolete',
                user,
                participant,
                participantDocumentActionsAllowed
              )
          }
        ]
      };
    });

  return (
    <div className="obsolete-documents-grid">
      <Grid
        gridId="obsolete-documents"
        columnDefinition={columnDefinition}
        rows={buildObsoleteRows(obsoleteDocuments)}
      />
    </div>
  );
};

class DocumentsService {
  static prepareData(items, participant, actions, user, participantDocumentActionsAllowed, timezone) {
    if (!items) return { signed: noAssignedDocs, assigned: noAssignedDocs };

    let { signed, assigned, obsolete, documentsMetadata } = this.prepareDocumentsMetadata(items);

    return {
      signed: prepareSignedGrid(signed, documentsMetadata, actions, user, participant, timezone),
      assigned: prepareAssignedGrid(
        assigned,
        documentsMetadata,
        actions,
        user,
        participant,
        participantDocumentActionsAllowed,
        timezone
      ),
      obsolete: prepareObsoleteGrid(
        obsolete,
        documentsMetadata,
        actions,
        user,
        participant,
        participantDocumentActionsAllowed,
        timezone
      )
    };
  }

  static prepareDocumentsMetadata(items) {
    let signed = [];
    let assigned = [];
    let obsolete = [];
    let documentsMetadata = [];

    if (items) {
      for (let row of items) {
        row.expirationDate = row.expirationDate
          ? DateHelper.timestampToHumanReadableString(Date.parse(row.expirationDate))
          : '';

        switch (row.classifier) {
          case 'OBSOLETE':
            obsolete.push(row);
            break;
          case 'PENDING':
            assigned.push(row);
            break;
          case 'COMPLETED':
            signed.push(row);
            break;
        }

        const documentMetadata = {
          consentGid: row.consentGid,
          consentName: row.consentName,
          consentingGid: row.consentingGid,
          status: row.consentingStatus,
          version: row.documentVersion
        };

        documentsMetadata.push(documentMetadata);
      }
    }

    return { documentsMetadata, signed, assigned, obsolete };
  }

  static buildDocumentFullName(documentName, documentVersion) {
    return `${documentName} - ${VERSION} ${documentVersion}`;
  }

  static getLabelForDocumentConfiguration(documentConfiguration) {
    let { mandatoryRead, mandatorySign, individuallyAssignable } = documentConfiguration;

    return mandatoryRead && mandatorySign && individuallyAssignable
      ? MANDATORY_FOR_SELECTED_LABEL
      : mandatoryRead && mandatorySign && !individuallyAssignable
        ? MANDATORY_FOR_ALL_LABEL
        : !(mandatoryRead && mandatorySign) && individuallyAssignable
          ? OPTIONAL_FOR_SELECTED_LABEL
          : !(mandatoryRead && mandatorySign) && !individuallyAssignable
            ? OPTIONAL_FOR_ALL_LABEL
            : undefined;
  }

  static getDocumentNameColumn(documentConfiguration, documentName) {
    return (
      <div>
        <div>{documentName}</div>
        <div className="configuration-label">
          {DocumentsService.getLabelForDocumentConfiguration(documentConfiguration)}
        </div>
      </div>
    );
  }

  static getTitleByAction(action) {
    return delete_options.pdf === action
      ? DELETE_PDF_TITLE
      : delete_options.document === action
        ? REMOVE_DOCUMENT_FROM_LIST
        : delete_options.entireDocument === action
          ? REMOVE_ENTIRE_DOCUMENT_FROM_LIST
          : null;
  }
}

export default DocumentsService;
